import React from "react";

/**
 * Extra attributes land on the root (the loading state's `aria-busy`, a test id).
 * The class list is not overridable: it is the height chain.
 */
export interface ListPageLayoutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  children: React.ReactNode;
}

/**
 * Root of a list view (header, search bar, table). Owns the page padding so every
 * list shares one spacing (CTX-1614): on desktop the top padding is 0 because the
 * distance from the header to the search row already comes from
 * `.main-container-global` + the card body + the card border (16 + 16 + 1 = 33px).
 * The height chain also lives here, so it has a single owner. `data-list-page`
 * is what `.main-container-global:has(> [data-list-page])` (index.css) keys on to
 * give that container a definite height, so `h-full` resolves and the card fills
 * the window. The selector is a child combinator, so nothing may wrap this root —
 * not even a `display: contents` div, which still counts in the DOM.
 * Below the card there is no padding of its own: the 16px under it is the
 * container's, matching the 16px above.
 * On desktop the root adds no side padding, so the card's gutter is
 * `.main-container-global`'s own (16px / 8px), the same as the Reflector V2 pages.
 */
export const ListPageLayout: React.FC<ListPageLayoutProps> = ({ children, ...rootProps }) => (
  <div
    {...rootProps}
    data-list-page=""
    className="relative p-1 sm:px-0 sm:pb-0 sm:pt-0 flex flex-col sm:overflow-hidden sm:h-full"
  >
    {children}
  </div>
);

export default ListPageLayout;
